import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/api";

const EMPTY = {
  brand: "", model: "", title: "", price: "", mileage: "", year: "", first_registration: "",
  power_ps: "", power_kw: "", fuel: "Benzin", transmission: "Automatik", body_type: "Sportwagen/Coupé",
  color: "", doors: "2", seats: "4", engine: "", displacement: "", acceleration: "", top_speed: "",
  consumption: "", co2: "", warranty: "", description: "",
  highlights: "", features: "", images: "",
  service_history: false, export: false, financeable: false, is_new_arrival: false,
};

const inputCls = "w-full bg-surface border border-line text-ink rounded-md px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand";
const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider text-faint font-semibold block mb-1.5">{label}</label>
    {children}
  </div>
);

export const VehicleForm = ({ open, onOpenChange, vehicle, onSave, saving }) => {
  const [f, setF] = useState(EMPTY);
  const [uploading, setUploading] = useState(0);

  useEffect(() => {
    if (!open) return;
    if (vehicle) {
      setF({
        ...EMPTY,
        ...Object.fromEntries(Object.entries(vehicle).map(([k, v]) => [k, v ?? ""])),
        price: String(vehicle.price), mileage: String(vehicle.mileage), year: String(vehicle.year),
        power_ps: String(vehicle.power_ps), power_kw: String(vehicle.power_kw),
        doors: String(vehicle.doors), seats: String(vehicle.seats),
        highlights: (vehicle.highlights || []).join("\n"),
        features: (vehicle.features || []).join("\n"),
        images: (vehicle.images || []).join("\n"),
      });
    } else {
      setF(EMPTY);
    }
  }, [open, vehicle]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

  const handleUpload = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    if (!files.length) return;
    setUploading(files.length);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd);
        const url = `${BACKEND_URL}${data.url}`;
        setF((prev) => ({ ...prev, images: prev.images ? `${prev.images}\n${url}` : url }));
      } catch (err) {
        toast.error(`Upload fehlgeschlagen: ${file.name}`);
      }
      setUploading((u) => u - 1);
    }
  };

  const removeImage = (idx) => {
    const arr = lines(f.images);
    arr.splice(idx, 1);
    setF({ ...f, images: arr.join("\n") });
  };

  const submit = (e) => {
    e.preventDefault();
    onSave({
      brand: f.brand, model: f.model, title: f.title,
      price: parseInt(f.price) || 0, mileage: parseInt(f.mileage) || 0, year: parseInt(f.year) || 0,
      first_registration: f.first_registration,
      power_ps: parseInt(f.power_ps) || 0,
      power_kw: parseInt(f.power_kw) || Math.round((parseInt(f.power_ps) || 0) * 0.7355),
      fuel: f.fuel, transmission: f.transmission, body_type: f.body_type, color: f.color,
      doors: parseInt(f.doors) || 2, seats: parseInt(f.seats) || 4,
      engine: f.engine, displacement: f.displacement, acceleration: f.acceleration,
      top_speed: f.top_speed, consumption: f.consumption, co2: f.co2,
      warranty: f.warranty, description: f.description,
      highlights: lines(f.highlights), features: lines(f.features), images: lines(f.images),
      service_history: !!f.service_history, export: !!f.export,
      financeable: false, is_new_arrival: !!f.is_new_arrival,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="vehicle-form-dialog" className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {vehicle ? "Fahrzeug bearbeiten" : "Neues Fahrzeug"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Marke *"><input data-testid="vform-brand" required value={f.brand} onChange={set("brand")} className={inputCls} /></Field>
            <Field label="Modell *"><input data-testid="vform-model" required value={f.model} onChange={set("model")} className={inputCls} /></Field>
          </div>
          <Field label="Inserats-Titel *"><input data-testid="vform-title" required value={f.title} onChange={set("title")} className={inputCls} /></Field>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="Preis (€) *"><input data-testid="vform-price" required type="number" value={f.price} onChange={set("price")} className={inputCls} /></Field>
            <Field label="Kilometer"><input data-testid="vform-mileage" type="number" value={f.mileage} onChange={set("mileage")} className={inputCls} /></Field>
            <Field label="Baujahr"><input data-testid="vform-year" type="number" value={f.year} onChange={set("year")} className={inputCls} /></Field>
            <Field label="EZ (MM/JJJJ)"><input value={f.first_registration} onChange={set("first_registration")} placeholder="06/2020" className={inputCls} /></Field>
            <Field label="Leistung (PS)"><input data-testid="vform-power" type="number" value={f.power_ps} onChange={set("power_ps")} className={inputCls} /></Field>
            <Field label="Leistung (kW)"><input type="number" value={f.power_kw} onChange={set("power_kw")} placeholder="auto" className={inputCls} /></Field>
            <Field label="Türen"><input type="number" value={f.doors} onChange={set("doors")} className={inputCls} /></Field>
            <Field label="Sitze"><input type="number" value={f.seats} onChange={set("seats")} className={inputCls} /></Field>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Field label="Kraftstoff">
              <select value={f.fuel} onChange={set("fuel")} className={inputCls}>
                {["Benzin", "Diesel", "Hybrid (Benzin/Elektro)", "Elektro"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Getriebe">
              <select value={f.transmission} onChange={set("transmission")} className={inputCls}>
                {["Automatik", "Automatik (PDK)", "Automatik (8-Gang)", "Schaltgetriebe (6-Gang)", "Schaltgetriebe (5-Gang)"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Karosserie">
              <select value={f.body_type} onChange={set("body_type")} className={inputCls}>
                {["Sportwagen/Coupé", "Limousine", "Cabrio/Roadster", "SUV/Geländewagen", "Kombi", "Kleinwagen"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Farbe"><input value={f.color} onChange={set("color")} className={inputCls} /></Field>
            <Field label="Motor"><input value={f.engine} onChange={set("engine")} placeholder="3.0L V6 Biturbo" className={inputCls} /></Field>
            <Field label="Hubraum"><input value={f.displacement} onChange={set("displacement")} placeholder="2.981 cm³" className={inputCls} /></Field>
            <Field label="0–100 km/h"><input value={f.acceleration} onChange={set("acceleration")} placeholder="4,0 s" className={inputCls} /></Field>
            <Field label="Vmax"><input value={f.top_speed} onChange={set("top_speed")} placeholder="301 km/h" className={inputCls} /></Field>
            <Field label="Verbrauch"><input value={f.consumption} onChange={set("consumption")} placeholder="8,0 l/100km" className={inputCls} /></Field>
            <Field label="CO₂"><input value={f.co2} onChange={set("co2")} placeholder="184 g/km" className={inputCls} /></Field>
            <Field label="Garantie"><input value={f.warranty} onChange={set("warranty")} className={inputCls} /></Field>
          </div>
          <Field label="Beschreibung">
            <textarea rows={3} value={f.description} onChange={set("description")} className={`${inputCls} resize-none`} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Highlights (eine pro Zeile)">
              <textarea data-testid="vform-highlights" rows={4} value={f.highlights} onChange={set("highlights")} placeholder={"Sport Chrono\nKeramikbremsen"} className={`${inputCls} resize-none`} />
            </Field>
            <Field label="Ausstattung (eine pro Zeile)">
              <textarea rows={4} value={f.features} onChange={set("features")} className={`${inputCls} resize-none`} />
            </Field>
          </div>
          <Field label="Fahrzeugbilder">
            {lines(f.images).length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-3" data-testid="vform-image-previews">
                {lines(f.images).map((url, i) => (
                  <div key={`${url}-${i}`} className="relative group">
                    <img src={url} alt="" loading="lazy" className="w-full h-16 object-cover rounded-lg bg-deep" />
                    <button
                      type="button"
                      data-testid={`vform-remove-image-${i}`}
                      onClick={() => removeImage(i)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-brand text-brand-ink flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Bild entfernen"
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label
              data-testid="vform-upload-label"
              className="flex items-center justify-center gap-2.5 border-2 border-dashed border-line rounded-xl py-5 cursor-pointer hover:border-silver transition-colors text-sm font-medium text-dim hover:text-ink"
            >
              <UploadCloud size={18} strokeWidth={1.7} />
              {uploading > 0 ? `${uploading} Bild(er) werden hochgeladen…` : "Bilder hochladen – vom Handy oder Rechner"}
              <input
                data-testid="vform-upload-input"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                disabled={uploading > 0}
                onChange={handleUpload}
              />
            </label>
            <textarea data-testid="vform-images" rows={2} value={f.images} onChange={set("images")} placeholder="Oder Bild-URLs einfügen (eine pro Zeile)" className={`${inputCls} resize-none mt-2 text-xs`} />
          </Field>
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            {[
              ["service_history", "Servicehistorie"],
              ["is_new_arrival", "Neu eingetroffen"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input type="checkbox" checked={!!f[k]} onChange={set(k)} className="w-4 h-4 accent-[#C81820]" />
                {label}
              </label>
            ))}
          </div>
          <button data-testid="vform-submit-btn" type="submit" disabled={saving}
            className="btn-pill w-full bg-action text-action-ink font-semibold py-3.5 text-sm hover:bg-action-hover disabled:opacity-50">
            {saving ? "Wird gespeichert…" : "Fahrzeug speichern"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default VehicleForm;
